import { CodeFile } from 'mini-code'
import { useEffect, useRef } from 'react'
import { useDualModeCodeStore } from './store.ts'

interface DualModeCodeUpdateManagerProps {
  codeFileA: CodeFile
  codeFileB: CodeFile
  channelMode: 'single' | 'dual'
}

export function DualModeCodeUpdateManager({
  codeFileA,
  codeFileB,
  channelMode,
}: DualModeCodeUpdateManagerProps) {
  // Use getState to avoid subscribing and causing re-renders
  const getDualModeCodeStore = useDualModeCodeStore.getState
  const prevSourceCodeA = useRef(codeFileA.value)
  const prevSourceCodeB = useRef(codeFileB.value)
  const scrollCheckIntervalRef = useRef<number | null>(null)
  const prevScrollStateA = useRef<{ scrollX: number; scrollY: number } | null>(null)
  const prevScrollStateB = useRef<{ scrollX: number; scrollY: number } | null>(null)

  useEffect(() => {
    if (channelMode !== 'dual') return

    const unsubscribeA = codeFileA.subscribe(() => {
      const newCode = codeFileA.value
      // Only update if code actually changed (not just selection/caret)
      if (prevSourceCodeA.current !== newCode) {
        prevSourceCodeA.current = newCode
        // Use getState to update without subscribing - this prevents App re-renders
        const { setDualModeCodeA } = getDualModeCodeStore()
        setDualModeCodeA(newCode, codeFileA.getState())
      }
    })

    const unsubscribeB = codeFileB.subscribe(() => {
      const newCode = codeFileB.value
      // Only update if code actually changed (not just selection/caret)
      if (prevSourceCodeB.current !== newCode) {
        prevSourceCodeB.current = newCode
        // Use getState to update without subscribing - this prevents App re-renders
        const { setDualModeCodeB } = getDualModeCodeStore()
        setDualModeCodeB(newCode, codeFileB.getState())
      }
    })

    // Initialize scroll state on first mount
    if (prevScrollStateA.current === null) {
      const stateA = codeFileA.getState()
      prevScrollStateA.current = {
        scrollX: stateA.scrollX ?? 0,
        scrollY: stateA.scrollY ?? 0,
      }
    }
    if (prevScrollStateB.current === null) {
      const stateB = codeFileB.getState()
      prevScrollStateB.current = {
        scrollX: stateB.scrollX ?? 0,
        scrollY: stateB.scrollY ?? 0,
      }
    }

    // Poll for scroll changes and save state
    const checkScrollChanges = () => {
      const stateA = codeFileA.getState()
      const stateB = codeFileB.getState()
      const scrollA = { scrollX: stateA.scrollX ?? 0, scrollY: stateA.scrollY ?? 0 }
      const scrollB = { scrollX: stateB.scrollX ?? 0, scrollY: stateB.scrollY ?? 0 }

      const { setDualModeCodeA, setDualModeCodeB } = getDualModeCodeStore()

      if (prevScrollStateA.current) {
        if (
          prevScrollStateA.current.scrollX !== scrollA.scrollX
          || prevScrollStateA.current.scrollY !== scrollA.scrollY
        ) {
          setDualModeCodeA(codeFileA.value, stateA)
          prevScrollStateA.current = scrollA
        }
      }
      else {
        setDualModeCodeA(codeFileA.value, stateA)
        prevScrollStateA.current = scrollA
      }

      if (prevScrollStateB.current) {
        if (
          prevScrollStateB.current.scrollX !== scrollB.scrollX
          || prevScrollStateB.current.scrollY !== scrollB.scrollY
        ) {
          setDualModeCodeB(codeFileB.value, stateB)
          prevScrollStateB.current = scrollB
        }
      }
      else {
        setDualModeCodeB(codeFileB.value, stateB)
        prevScrollStateB.current = scrollB
      }
    }

    scrollCheckIntervalRef.current = setInterval(checkScrollChanges, 500) as unknown as number

    return () => {
      unsubscribeA()
      unsubscribeB()
      if (scrollCheckIntervalRef.current) {
        clearInterval(scrollCheckIntervalRef.current)
        scrollCheckIntervalRef.current = null
      }
      prevScrollStateA.current = null
      prevScrollStateB.current = null
    }
  }, [channelMode, codeFileA, codeFileB, getDualModeCodeStore])

  return null
}

